import {
  ApiTags,
  Patient,
  PatientDetail,
  PatientStatusOption,
  PatientStatusOptions,
  PatientsRequest,
  PatientsResponse,
  Thresholds,
} from '@/services/api.types';
import { createApi } from '@reduxjs/toolkit/query/react';
import authedQuery from '@/services/authed.query';

import { PatientCreateResponse } from '@/models/patients';
import {
  patientRowFromWire,
  snakeKeysFromWire,
  statusSlugToBare,
} from '@/services/patient-slug';
import _ from 'lodash';

export const patientApi = createApi({
  reducerPath: 'patientApi',
  baseQuery: authedQuery,
  // Batch 007 / ADR-B5: this slice had NO tagTypes and therefore no invalidation
  // at all — every patient-domain list relied on a 60s poll to notice writes.
  tagTypes: [ApiTags.Patient, ApiTags.PatientList, ApiTags.PatientPinnedStatus],
  endpoints: builder => ({
    // GET /v1/patients — paged, sorted, filtered (ADR-A6). The repo's first
    // paged query; 008-011 copy this shape.
    //
    // Empty values are dropped here rather than in the serialiser so the
    // serialiser keeps the default's semantics for every other endpoint. Arrays
    // reach the wire as repeated keys via authedQuery's paramsSerializer.
    //
    // `false` is dropped alongside them, and that is a contract reading rather
    // than tidiness: both booleans this request carries (`pinned`,
    // `all_patients`) are proto3 bools, where an explicit `false` and an absent
    // field are the same value on arrival. Serialising one would put
    // `all_patients=false` on the wire — a parameter that reads like a
    // deliberate "not everybody" while meaning nothing at all, and the exact
    // shape a `false` leaking out of the caller's tri-state would take.
    getPatients: builder.query<PatientsResponse, PatientsRequest>({
      query: (params: PatientsRequest) => ({
        url: '/v1/patients',
        method: 'GET',
        params: _.omitBy(
          params,
          value =>
            _.isNil(value) ||
            value === '' ||
            value === false ||
            (_.isArray(value) && !value.length)
        ),
      }),
      // Every row crosses the slug boundary here, in ONE place — see
      // patient-slug.ts for why that matters clinically.
      transformResponse: (response: any) => ({
        ...response,
        patients: (response.patients ?? []).map(patientRowFromWire),
        // The envelope carries `totalRecords` / `totalPages` on the wire.
        page: snakeKeysFromWire(response.page ?? {}),
      }),
      providesTags: [{ type: ApiTags.PatientList, id: 'LIST' }],
    }),

    // GET /v1/patients/options/statuses — this tenant's enabled status
    // vocabulary (ADR-A3).
    //
    // Authenticated on purpose: it is a post-login business dictionary, not the
    // pre-login render config. The 4-segment path also cannot be confused with
    // `/v1/patients/{id}`, which compiles to a single-segment match.
    //
    // The response is reshaped to `{ status: [...] }` — the field name the two
    // existing consumers already read — so no render code changes.
    getPatientStatusOptions: builder.query<PatientStatusOptions, void>({
      query: () => ({ url: '/v1/patients/options/statuses', method: 'GET' }),
      transformResponse: (response: any) => ({
        status: (response.statuses ?? []).map((option: any) => {
          const snake = snakeKeysFromWire(option);
          return { ...snake, status: statusSlugToBare(snake.status) as string };
        }),
      }),
    }),

    // PUT /v1/patients/{id}/pin — idempotent in BOTH directions (ADR-A10).
    //
    // Replaces the legacy POST add + POST cancel pair, which took a batch of ids
    // and deduplicated in the BFF because the table had no unique constraint at
    // all. The UI surface was always a single per-row toggle.
    //
    // ⚠️ Invalidates the pinned strip and the patient's detail, but NOT the paged
    // list (ADR-B5's Skeptic, overruling the ADR's first draft): invalidating the
    // list would add a visible loading state and, under server-side paging, could
    // reshuffle rows under the cursor on the cheapest click in the app.
    //
    // That ruling rests on a premise — every list row already carries `pinned`,
    // so the icon can be updated from the mutation itself rather than by
    // refetching. `onQueryStarted` below is that premise; until it existed the
    // comment was a claim with no code behind it, the cached row kept the value
    // it held before the click, and the row icon only flipped on a full reload.
    putPatientPin: builder.mutation<
      { pinned: boolean },
      { id: string; pinned: boolean }
    >({
      query: ({ id, pinned }) => ({
        url: `/v1/patients/${id}/pin`,
        method: 'PUT',
        body: { pinned },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: ApiTags.PatientPinnedStatus, id: 'LIST' },
        { type: ApiTags.Patient, id },
      ],
      // Patch the row flag in EVERY cached page/filter combination instead of
      // refetching any of them. Three call sites share this endpoint — the list
      // row, the detail header and the pinned card — so this one place serves
      // all three and none of them owns cache logic.
      //
      // Pessimistic, not optimistic: the patch runs after `queryFulfilled` and
      // writes what the server confirmed. The click already renders a per-row
      // spinner while the request is in flight, so there is nothing to gain from
      // guessing, and a rejected toggle must not leave a lie in the cache.
      //
      // Why every combination and not just the visible one: the list is
      // subscribed without `refetchOnMountOrArgChange`, so switching to a
      // parameter combination that is already cached serves that entry as-is.
      // Patching only the entry on screen puts the stale flag back on the next
      // filter change.
      //
      // `selectInvalidatedBy` is the enumeration util available on this repo's
      // @reduxjs/toolkit (1.9.0 has no `selectCachedArgsForQuery`). It is a plain
      // selector over the cache — reading the entries that carry a tag, NOT
      // invalidating them — so the ruling above is untouched. Filtered by
      // endpoint name because the tag is a list-level tag and another endpoint
      // could join it later.
      async onQueryStarted(
        { id, pinned },
        { dispatch, queryFulfilled, getState }
      ) {
        let confirmed: boolean;
        try {
          const { data } = await queryFulfilled;
          // The contract answers `{pinned}`. Falling back to the requested value
          // if that boolean is ever absent is safe precisely because the PUT is
          // idempotent (ADR-A10): a fulfilled response means the desired state
          // holds. An unguarded read would write `undefined` into the row and
          // blank the icon.
          confirmed = typeof data?.pinned === 'boolean' ? data.pinned : pinned;
        } catch {
          // Rejected toggle: nothing changed server-side, so nothing changes
          // here. The await must be caught — an uncaught one takes the process
          // down (batch 016's measurement).
          return;
        }

        patientApi.util
          .selectInvalidatedBy(getState(), [
            { type: ApiTags.PatientList, id: 'LIST' },
          ])
          .filter(entry => entry.endpointName === 'getPatients')
          .forEach(({ originalArgs }) => {
            dispatch(
              patientApi.util.updateQueryData(
                'getPatients',
                originalArgs,
                draft => {
                  const row = draft.patients?.find(
                    (patient: Patient) => patient.id === id
                  );
                  if (row) {
                    row.pinned = confirmed;
                  }
                }
              )
            );
          });
      },
    }),

    // GET /v1/me/patients/pinned — the caller's own pinned patients, UNPAGED.
    //
    // Under /v1/me rather than /v1/patients/pinned: that path would have the same
    // segment count as /v1/patients/{id}, and the backend's router matches by
    // registration order with no "static segment beats variable" rule, so
    // correctness would depend on rpc declaration order. It also reads truer —
    // the pin table is per-clinician, so this is the caller's list rather than a
    // subcollection of the patient resource.
    // ⚠️ The rows are `PatientSummary` — the SAME contract message the paged
    // list returns — so they are typed `Patient`, the transcription of that
    // message, and not the legacy `PatientSimpleAlarmInfo`. That legacy type
    // declared `last_alarm: string` as REQUIRED plus `birth` / `patient_groups`,
    // none of which this contract serves; typing the response with it made every
    // one of those reads invisible to tsc, and the unguarded
    // `last_alarm.toLowerCase()` in the card blanked the entire application as
    // soon as a clinician had one pinned patient.
    getPinnedPatients: builder.query<{ patients: Patient[] }, void>({
      query: () => ({ url: '/v1/me/patients/pinned', method: 'GET' }),
      transformResponse: (response: any) => ({
        patients: (response.patients ?? []).map(patientRowFromWire),
      }),
      providesTags: [{ type: ApiTags.PatientPinnedStatus, id: 'LIST' }],
    }),
    createPatient: builder.mutation<
      PatientCreateResponse,
      PatientCreateResponse
    >({
      query: (patient: PatientCreateResponse) => ({
        url: '/v1/patients',
        method: 'POST',
        body: patient,
      }),
      // Requests carry snake_case proto field names; only RESPONSES come back
      // camelCase. See patient-slug.ts.
      transformResponse: patientRowFromWire,
      invalidatesTags: [{ type: ApiTags.PatientList, id: 'LIST' }],
    }),
    editPatientById: builder.mutation<
      PatientCreateResponse,
      PatientCreateResponse
    >({
      query: (patient: any) => ({
        url: `/v1/patients/${patient?.id}`,
        method: 'PUT',
        body: patient,
      }),
      transformResponse: patientRowFromWire,
      // PD-2952: the pinned strip renders the same PatientSummary rows (name,
      // cpr, organizations…), so an edit must refresh it too — the strip's
      // cache key is constant and the component never unmounts from the Home
      // layout, so nothing else would pick the edit up before the next poll.
      invalidatesTags: (result, error, patient: any) => [
        { type: ApiTags.PatientList, id: 'LIST' },
        { type: ApiTags.Patient, id: patient?.id },
        { type: ApiTags.PatientPinnedStatus, id: 'LIST' },
      ],
    }),
    getPatientPageDetailById: builder.query<PatientDetail, string>({
      query: (patientId: string) => ({ url: `/v1/patients/${patientId}` }),
      transformResponse: patientRowFromWire,
      providesTags: (result, error, patientId) => [
        { type: ApiTags.Patient, id: patientId },
      ],
    }),
  }),
});

export const {
  useGetPatientsQuery,
  useGetPatientStatusOptionsQuery,
  usePutPatientPinMutation,
  useGetPinnedPatientsQuery,
  useCreatePatientMutation,
  useEditPatientByIdMutation,
  useGetPatientPageDetailByIdQuery,
} = patientApi;

export default patientApi;
